import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import {
	BookingListItemDto,
	IBookAppointmentObject,
	IBookingSearchFilters,
	IChildrenBookingTemplatesRequest,
} from 'models/bookappointment';
import { IStore } from 'redux/IStore';
import { connect, Dispatch } from 'react-redux';
import { reduxForm, getFormSyncErrors } from 'redux-form';
import { RouteComponentProps } from 'react-router';
import { InjectedFormProps, ITranslateProps } from 'models/common';
import { IUserPreferences } from 'models/user/userpreferences';
import { searchAvailableBookings } from 'redux/modules/bookappointment';
import moment = require('moment');
import { timeAsNumber, timeFromNumber } from 'helpers/bookingTemplates';
import { detectIncompatibleBrowsers, formatCurrency, formatDuration } from 'helpers/common';
import { PortalPageSection } from 'components/PortalStructure';
import { IOffice } from 'models/shop/office';
import { IServicesList } from 'models/shop/shopBookingTemplates';
import { fullServiceName } from '../helpers';
import { IOfficeMember } from 'models/shop/members';
import { Button } from 'components/Common/Button';
import renderIcon from 'helpers/renderIcon';
import { closeModal, showModal } from 'redux/modules/modal';
import { setDateFilter } from 'redux/modules/shop/offices';
import ResourceDetails from './ResourceDetails';
import DateSelectionContainer from './FilterFileds/DateSelectionContainer';
import { momentWithTimezone } from 'redux/modules/shop/helpers';

interface IParams {
	shopId: string;
	officeId: string;
}

interface IProps
	extends ReturnType<typeof stateToProps>,
		ReturnType<typeof dispatchToProps>,
		ITranslateProps,
		InjectedFormProps<IBookAppointmentObject>,
		RouteComponentProps<IParams, {}> {
	route: any;
	servicesParentIds: string[];
	userPreferences?: IUserPreferences;
	mobileShopOrderDetailsElement: () => JSX.Element;
	onSubmit?: (form: IBookAppointmentObject) => void;
	setStaffForService: (variantParentId: string, resourceId: string | null) => boolean;
	selectStaffChange: (variantParentId: string) => null;
	previousPage?: () => void;
}

interface IState {
	timeReset?: () => void;
	showBookingDetails: boolean;
	selectedDate: string;
	dateSelectionArray: string[];
	dateSelectionArrayMobile: string[];
	dateSelectionObject: { [date: string]: BookingListItemDto[] };
	timezone: string;
	lastShownDate: string;
}

const formKeys = {
	timeFrom: 'filter.timeFrom',
	timeTo: 'filter.timeTo',
	date: 'filter.date',
	selectedAppointment: 'selectedAppointment',
} as const;

const roundDateTime = (
	time: moment.Moment,
	mode: 'round' | 'ceil' | 'floor' = 'round',
	timezone?: string,
): moment.Moment => {
	const minutes =
		Math[mode]((time.minute() + time.seconds() / 60) / c.MIN_TIME_INTERVAL_MINUTES) * c.MIN_TIME_INTERVAL_MINUTES;
	return momentWithTimezone(timezone, time).seconds(0).minutes(minutes);
};

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class BookAppointmentStepScheduleForm extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);
		this.onSubmit = this.onSubmit.bind(this);
		this.state = {
			showBookingDetails: false,
			selectedDate: null,
			dateSelectionArray: [],
			dateSelectionArrayMobile: [],
			dateSelectionObject: {},
			timezone: null,
			lastShownDate: undefined,
		};
	}

	public componentDidMount() {
		const bookedOffice = (this.props.shopCard?.offices || []).find((o) => o.id === this.props.params.officeId);
		const timezone = bookedOffice?.zoneInfo;
		this.setState({ timezone });

		if (detectIncompatibleBrowsers()) {
			document.body.scrollTo({ top: 0, left: 0 });
		} else {
			window.scrollTo({ top: 0, left: 0 });
		}

		this.setInitialDates(timezone).then(() => {
			this.loadData();
		});
	}

	public componentDidUpdate(prevProps: Readonly<IProps>): void {
		const { change, currentFormValues, isFetching, availableItems } = this.props;
		const prevStaff = prevProps.currentFormValues.selectStaff;
		const nextStaff = this.props.currentFormValues.selectStaff;

		if (JSON.stringify(prevStaff) !== JSON.stringify(nextStaff)) {
			this.loadData();
		}

		if (prevProps.isFetching && !isFetching && availableItems && currentFormValues.selectedAppointment) {
			const selectedId = currentFormValues.selectedAppointment.id;

			if (!availableItems.some((i) => i.id === selectedId)) {
				change(formKeys.selectedAppointment, null);
			}
		}
	}

	private loadData(daysToLoad: number = 7) {
		const requests = this.searchRequests;

		if (!requests.length) {
			console.warn('Cannot build any search request');
			return;
		}

		this.props.searchAvailableBookings(requests).then(() => {
			const { availableItems } = this.props;
			const { dateSelectionArray } = this.state;
			const objItems: { [date: string]: BookingListItemDto[] } = {};

			for (let day = 0; day < daysToLoad; day++) {
				objItems[dateSelectionArray[day]] = [];
			}

			availableItems.forEach((el) => {
				const elStartTime = momentWithTimezone(this.state.timezone, el.schedule).startOf('day').toISOString();
				if (elStartTime in objItems) {
					objItems[elStartTime].push(el);
				}
			});

			// Set first date with available bookings when found else fill dateSelectionObject
			if (!this.state.selectedDate) {
				let firstDate = null;

				for (const [key, value] of Object.entries(objItems)) {
					if (value.length > 0) {
						firstDate = key;
						break;
					}
				}

				this.setState({
					dateSelectionObject: { ...this.state.dateSelectionObject, ...objItems },
					selectedDate: firstDate,
				});
			} else {
				this.setState({ dateSelectionObject: { ...this.state.dateSelectionObject, ...objItems } });
			}
		});
	}

	private get minTime(): moment.Moment {
		const { dateSelectionArray } = this.state;

		return momentWithTimezone(this.state.timezone, dateSelectionArray[0]).startOf('day');
	}

	private get maxTime() {
		const { dateSelectionArray } = this.state;

		return momentWithTimezone(this.state.timezone, dateSelectionArray[0])
			.endOf('day')
			.add(-c.MIN_TIME_INTERVAL_MINUTES, 'minute');
	}

	private get dateFilter(): [moment.Moment, moment.Moment] {
		const timeFrom = timeAsNumber(this.minTime);
		const timeTo = timeAsNumber(this.maxTime);
		const selectedDateStart = this.minTime;
		let selectedDateEnd = this.minTime.clone().add(6, 'days');

		if (this.state.lastShownDate) {
			const lastShown = momentWithTimezone(this.state.timezone, this.state.lastShownDate);
			if (lastShown.isAfter(selectedDateEnd)) {
				selectedDateEnd = lastShown;
			}
		}

		return [
			roundDateTime(timeFromNumber(timeFrom, selectedDateStart), 'ceil', this.state.timezone),
			roundDateTime(timeFromNumber(timeTo, selectedDateEnd), 'ceil', this.state.timezone),
		];
	}

	private get searchRequests(): IBookingSearchFilters[] {
		const { currentFormValues, shopBookingTemplates, servicesParentIds } = this.props;
		const dateFilter = this.dateFilter;
		const requests: IBookingSearchFilters[] = [];
		const parentIds = currentFormValues.selectStaff ? Object.keys(currentFormValues.selectStaff) : servicesParentIds;

		const addServiceRequest = (
			serviceId: string,
			resourceId?: string,
			isVariantWithoutResource?: boolean,
			variantParentId?: string,
			availableResourceIds?: string[],
			childrenBookingTemplates?: IChildrenBookingTemplatesRequest[],
		) => {
			if (!serviceId) {
				return;
			}

			// ...otherwise just request full interval (e.g. from now until Wed midnight)
			const dateFrom =
				momentWithTimezone(this.state.timezone).startOf('day') >=
				momentWithTimezone(this.state.timezone, dateFilter[0]).startOf('day')
					? moment
							.max(momentWithTimezone(this.state.timezone).startOf('day'), momentWithTimezone(this.state.timezone))
							.add(15, 'minutes')
							.startOf('minute')
					: moment.max(
							momentWithTimezone(this.state.timezone, dateFilter[0]).startOf('day'),
							momentWithTimezone(this.state.timezone),
					  );

			requests.push({
				serviceId,
				dateFrom: dateFrom.toISOString(),
				dateTo: dateFilter[1].toISOString(),
				resourceId,
				isVariantWithoutResource,
				variantParentId,
				availableResourceIds,
				childrenBookingTemplates,
				includePartiallyUnavailableResources: false,
			});
		};

		for (const parentId of parentIds) {
			if (!currentFormValues.selectStaff?.[parentId]) {
				// Any staff member
				const services = shopBookingTemplates.filter((ser) => ser.variantParentId === parentId);
				// if normal service / else variation service
				services.map((ser) => {
					addServiceRequest(
						ser.id,
						null,
						services.length !== 1,
						ser.variantParentId,
						ser.resources.map((rV) => (rV as IOfficeMember)?.id || (rV as string)),
					);
				});
			} else {
				for (const serviceId in currentFormValues.selectStaff[parentId]) {
					const service = shopBookingTemplates.find((s) => s.id === serviceId);
					const parentService = shopBookingTemplates.find((ps) => ps.variantParentId === parentId);
					const services = shopBookingTemplates.filter((ser) => ser.variantParentId === parentId);
					const resourceId = currentFormValues.selectStaff[parentId][serviceId];

					// If bundle (bundle form values go 1 level deeper because of their children BT's)
					if (parentService?.childrenBookingTemplates?.length > 0) {
						const childrenBookingTemplates: IChildrenBookingTemplatesRequest[] = [];

						services.map((ser) => {
							if (ser.childrenBookingTemplates.length > 0) {
								ser.childrenBookingTemplates.map((cbt) => {
									const resourceId = currentFormValues.selectStaff[parentId][cbt.id] || null;

									childrenBookingTemplates.push({ item1: cbt.id, item2: resourceId }); // item1: bookingTemplateId, item2: resourceId
								});
							}
						});

						addServiceRequest(
							parentService.id,
							null,
							services.length !== 1,
							parentService.variantParentId,
							parentService.resources.map((rV) => (rV as IOfficeMember)?.id || (rV as string)),
							childrenBookingTemplates,
						);
					} else {
						// Else service
						addServiceRequest(
							serviceId,
							resourceId,
							services.length !== 1,
							service.variantParentId,
							service.resources.map((rV) => (rV as IOfficeMember)?.id || (rV as string)),
						);
					}
				}
			}
		}

		return requests;
	}

	public onSubmit(form: IBookAppointmentObject) {
		if (!this.props.currentFormValues.selectedAppointment) {
			return;
		}
		this.props.onSubmit(JSON.parse(JSON.stringify(form)));
	}

	private get staffById() {
		const { shopCard } = this.props;
		const staffById: { [userId: string]: IOffice['staff'][number] } = {};
		for (const office of shopCard.offices || []) {
			for (const staff of (office.staff || []) as IOfficeMember[]) {
				staffById[staff.id] = staff;
			}
		}
		return staffById;
	}

	private get servicesByIdOrParentId() {
		const { shopBookingTemplates, serviceGroups, t } = this.props;
		const servicesById: { [id: string]: IServicesList & { fullName: JSX.Element } } = {};
		for (const service of shopBookingTemplates || []) {
			const fullServiceNameVar = fullServiceName(service, serviceGroups, t); // index 0 is service category, 1 is service group, 2 is service name
			const fullService = {
				...service,
				fullName: (
					<>
						{/**{fullServiceNameVar[0]} <span className="serviceFullNameSlash">/</span>**/} {fullServiceNameVar[1]}{' '}
						<span className="serviceFullNameSlash">/</span> {fullServiceNameVar[2]}
					</>
				),
			};

			servicesById[service.id] = fullService;

			if (service.variantParentId) {
				servicesById[service.variantParentId] = fullService;
			}

			// If bundle
			if (service.childrenBookingTemplates.length > 0) {
				service.childrenBookingTemplates.map((cbt) => {
					const fullChildrenServiceNameVar = fullServiceName(cbt, serviceGroups, t); // index 0 is service category, 1 is service group, 2 is service name
					const fullChildrenService = {
						...cbt,
						fullName: (
							<>
								{/**{fullChildrenServiceNameVar[0]} <span className="serviceFullNameSlash">/</span>**/}{' '}
								{fullServiceNameVar[2]} <span className="serviceFullChildrenNameSlash">/</span>{' '}
								{fullChildrenServiceNameVar[2]}
							</>
						),
					};

					// Add child booking template to servicesById
					servicesById[`${service.id}:${cbt.id}`] = fullChildrenService; // key is bundle booking template id : child booking template id for separating from other bundles and services
				});
			}
		}

		return servicesById;
	}

	private renderAvailableAppointments() {
		const { t, isFetching, currentFormValues, change, shopCard } = this.props;
		const { selectedDate, dateSelectionObject } = this.state;
		const currencyKey = shopCard.currencyKey;

		if ((!isFetching && !selectedDate) || (!isFetching && selectedDate && !dateSelectionObject[selectedDate].length)) {
			return (
				<div className="bookingAppointment__scheduleStep__noResults">{t('BookAppointment_Schedule_NoResults')}</div>
			);
		}

		// const officesById = this.officesById;
		const servicesById = this.servicesByIdOrParentId; // questionable (doubling services / filter if needed)
		const staffById = this.staffById;
		const items: BookingListItemDto[] = dateSelectionObject[selectedDate] || [];

		const renderItem = (item: BookingListItemDto): JSX.Element => {
			let schedule: JSX.Element = null;
			const itemDate: BookingListItemDto = item;
			const service = servicesById[item?.availableBookings[0].bookingTemplateId];

			if (service && itemDate.schedule) {
				const start = momentWithTimezone(this.state.timezone, item.schedule);
				const end = start.clone().add(itemDate.duration, 'seconds');
				schedule = (
					<>
						<p className="bookingAppointment__scheduleStep__mainDetails__time">{start.format('LT')}</p>
						<p className="bookingAppointment__scheduleStep__mainDetails__timeSubtext">
							{formatDuration(itemDate.duration)} {t('BookAppointment_Schedule_Duration')} {end.format('LT')}
						</p>
					</>
				);
			} else if (itemDate.schedule) {
				schedule = (
					<p className="bookingAppointment__scheduleStep__mainDetails__time">
						{momentWithTimezone(this.state.timezone, itemDate.schedule).format('LT')}
					</p>
				);
			}

			const isActive =
				currentFormValues.selectedAppointment &&
				currentFormValues.selectedAppointment.availableBookings[0].id === item.availableBookings[0].id;
			const beforeDiscount = item.availableBookings.reduce(
				(prev, curr) =>
					prev +
					(JSON.parse(servicesById[curr.bookingTemplateId].customData || '{}')?.priceSum ||
						servicesById[curr.bookingTemplateId].originalPrice),
				0,
			);

			return (
				<div key={item.availableBookings[0]?.id}>
					<PortalPageSection
						className="bookingAppointment__scheduleStep__bookingCard"
						variant={isActive ? 'active' : 'normal'}
						onClick={() => change(formKeys.selectedAppointment, item)}
					>
						<div className="bookingAppointment__scheduleStep__mainDetails">
							{schedule}
							<p className="bookingAppointment__scheduleStep__mainDetails__price">
								{beforeDiscount && beforeDiscount > item.price && (
									<span className="bookingAppointment__scheduleStep__mainDetails__price__beforeDiscount">
										{formatCurrency(currencyKey, beforeDiscount)}
									</span>
								)}
								{formatCurrency(currencyKey, item.price)}
							</p>
						</div>
						<ResourceDetails bookingItem={item} staffById={staffById} servicesById={servicesById} />
					</PortalPageSection>
				</div>
			);
		};

		const elements: JSX.Element[] = [];
		if (items.length !== 0) {
			items.map((item, i) => {
				elements.push(<React.Fragment key={item.schedule + i}>{renderItem(item)}</React.Fragment>);
			});
		}

		return elements;
	}

	private setInitialDates = (timezone?: string): Promise<any> => {
		const currentDay = momentWithTimezone(timezone).startOf('day');
		const timeArray = [];
		const timeArrayMobile = [];

		for (let day = 0; day < 7; day++) {
			if (day === 0) {
				timeArray.push(currentDay.toISOString());
			} else {
				timeArray.push(currentDay.clone().add(day, 'days').toISOString());
			}
		}

		for (let day = 0; day < 60; day++) {
			if (day === 0) {
				timeArrayMobile.push(currentDay.toISOString());
			} else {
				timeArrayMobile.push(currentDay.clone().add(day, 'days').toISOString());
			}
		}

		this.setState({ dateSelectionArray: [...timeArray], dateSelectionArrayMobile: [...timeArrayMobile] });
		return Promise.resolve();
	};

	private setDates = (newDateSelectionArray: string[], daysToLoad: number, dateIndex: number) => {
		const { timezone, dateSelectionArrayMobile } = this.state;

		// Check here if loadData is needed by going through the dateSelectionObject state
		let isAlreadyLoaded: boolean = false;

		newDateSelectionArray.findIndex((newDate) => {
			if (newDate in this.state.dateSelectionObject) {
				isAlreadyLoaded = true;
				return true; // stop loop when found
			}
		});

		this.setState(
			{ dateSelectionArray: [...newDateSelectionArray], lastShownDate: dateSelectionArrayMobile?.[dateIndex] },
			() => {
				if (!isAlreadyLoaded) {
					this.loadData(daysToLoad);

					const lastMobileDate = dateSelectionArrayMobile[dateSelectionArrayMobile.length - 1];
					const loadMoreMobileDatesArray = [];

					if (dateIndex && dateSelectionArrayMobile.length - dateIndex < 30) {
						// Load more date strings into mobile array
						for (let i = 0; i < 60; i++) {
							loadMoreMobileDatesArray.push(
								momentWithTimezone(timezone, lastMobileDate)
									.add(i + 1, 'days')
									.toISOString(),
							);
						}
						this.setState({ dateSelectionArrayMobile: [...dateSelectionArrayMobile, ...loadMoreMobileDatesArray] });
					}
				}
			},
		);
	};

	private setSelectedDate = (date: string) => {
		this.setState({ selectedDate: date });
	};

	public render() {
		const { t, handleSubmit, currentFormValues, isFetching } = this.props;

		return (
			<form onSubmit={handleSubmit(this.onSubmit)} className="bookingAppointment__scheduleStep">
				<h1>{t('BookAppointment_Schedule_Title')}</h1>
				<DateSelectionContainer
					dateSelectionArray={this.state.dateSelectionArray}
					dateSelectionArrayMobile={this.state.dateSelectionArrayMobile}
					setDates={this.setDates}
					setSelectedDate={this.setSelectedDate}
					selectedDate={this.state.selectedDate}
					roundDateTime={roundDateTime}
					dateSelectionObject={this.state.dateSelectionObject}
					timezone={this.state.timezone}
					isFetching={this.props.isFetching}
				/>
				<div className="bookingAppointment__scheduleStep__cardsContainer">{this.renderAvailableAppointments()}</div>
				{this.state.showBookingDetails && <div className="bookingAppointment__shadow"></div>}
				<div className="bookingAppointment__bottomContent">
					<div className="container">
						<div className="row">
							<div className="col-lg-9 col-xl-7 offset-lg-3">
								<div className="bookingAppointment__bottomContent__bookingDetails">
									<div
										className="detailsSwitch"
										onClick={() => this.setState({ showBookingDetails: !this.state.showBookingDetails })}
									>
										<span>{t('BookAppointment_Booking_Details_Title')}</span>
										{this.state.showBookingDetails ? renderIcon('arrow_right') : renderIcon('arrow_left')}
									</div>
									<div className="detailsContent">
										{this.state.showBookingDetails && this.props.mobileShopOrderDetailsElement()}
									</div>
								</div>
								<div className="bookingAppointment__bottomContent__multiple">
									<Button onClick={this.props.previousPage}>
										{renderIcon('arrow_left_small')} {t('BSPortal_Common_Back')}
									</Button>
									<Button type="submit" disabled={isFetching || !currentFormValues.selectedAppointment}>
										{t('BSPortal_Common_Continue')}
									</Button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</form>
		);
	}
}

let BookAppointmentStepSchedule = reduxForm({
	form: c.FORM_NAME_BOOKAPPOINTMENT,
	destroyOnUnmount: false,
	forceUnregisterOnUnmount: true,
})(BookAppointmentStepScheduleForm);

const stateToProps = (state: IStore) => {
	let currentFormValues: IBookAppointmentObject = null;
	if (state.form[c.FORM_NAME_BOOKAPPOINTMENT] && state.form[c.FORM_NAME_BOOKAPPOINTMENT].values) {
		currentFormValues = state.form[c.FORM_NAME_BOOKAPPOINTMENT].values;
	}

	return {
		currentFormValues,
		shopBookingTemplates: state.shopBookingTemplates?.services || [],
		availableItems: state.bookAppointment?.availableItems || [],
		isFetching: state.bookAppointment?.isFetching,
		shopCard: state.shopcard.shopcard,
		allItems: (state.bookAppointment?.availableItems || []).length,
		formErrors: getFormSyncErrors(c.FORM_NAME_BOOKAPPOINTMENT)(state),
		serviceGroups: state.serviceGroups.serviceGroups || [],
	};
};

const dispatchToProps = (dispatch: Dispatch<IStore>) => ({
	searchAvailableBookings: (requests: IBookingSearchFilters[]) => dispatch(searchAvailableBookings(requests)),
	setDateFilter: (dateFilter: number) => dispatch(setDateFilter(dateFilter)),
	showModal: (type: string, modalProps: any) => dispatch(showModal(type, modalProps)),
	closeModal: () => dispatch(closeModal()),
});

BookAppointmentStepSchedule = connect(stateToProps, dispatchToProps)(BookAppointmentStepSchedule);

export { BookAppointmentStepSchedule };
